/**
 * field-requirements-store — interface com /api/v1/admin/field-requirements
 * (FEAT-V2-001).
 *
 * Backend é source-of-truth. Hooks:
 *   - useFieldRequirements(workflow?, entityType?)  — lista (admin only)
 *   - useFieldRequirementsLookup(workflow, entityType) — mapa fieldName→entry
 *     pra DealFormDialog/NewClientDialog ler "este campo é obrigatório?"
 *     em O(1)
 *   - useFieldRequirementActions()                  — upsert
 *
 * Cache: staleTime 5min (config muda raramente; é admin manual). React
 * Query invalida tudo após upsert.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type FieldRequirementEntityType = 'card' | 'client';

export interface FieldRequirement {
  /** F1 (mig 470): '' = régua global; senão, etiqueta exata do Omie. */
  etiqueta?: string;
  workflowSlug: string;
  entityType: FieldRequirementEntityType;
  fieldName: string;
  required: boolean;
  fixed: boolean;
  atualizadoEmIso: string;
  atualizadoPor: string | null;
}

export interface UpsertFieldRequirementInput {
  /** F2 (mig 470): '' = régua global; senão, a etiqueta exata do Omie. */
  etiqueta?: string;
  workflowSlug: string;
  entityType: FieldRequirementEntityType;
  fieldName: string;
  required: boolean;
}

// ─── Keys ───────────────────────────────────────────────────────────────

export const FIELD_REQ_KEYS = {
  all: ['v1', 'admin', 'field-requirements'] as const,
  list: (workflow: string | null, entityType: string | null) =>
    ['v1', 'admin', 'field-requirements', { workflow, entityType }] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────

interface UseListOptions {
  workflow?: string | null;
  entityType?: FieldRequirementEntityType | null;
  /** Se true, dispara mesmo sem token (default false). */
  enabled?: boolean;
}

export function useFieldRequirements(options: UseListOptions = {}) {
  const { workflow = null, entityType = null, enabled = true } = options;
  const params = new URLSearchParams();
  if (workflow) params.set('workflow', workflow);
  if (entityType) params.set('entity_type', entityType);
  const qs = params.toString();
  // AUDITORIA-V2-002 / C1 (2026-05-25): endpoint canônico é PÚBLICO (qualquer
  // user autenticado pode ler). ANTES: `/api/v1/admin/field-requirements` —
  // exigia admin e bloqueava vendedor → BUG-V2-055 persistente.
  // Alias legacy `/admin/field-requirements` continua válido 30d com warn log.
  const path = qs
    ? `/api/v1/field-requirements?${qs}`
    : '/api/v1/field-requirements';

  return useQuery({
    queryKey: FIELD_REQ_KEYS.list(workflow, entityType),
    queryFn: () => api.get<FieldRequirement[]>(path),
    enabled: enabled && hasToken(),
    staleTime: 5 * 60_000, // 5 min — config muda raramente
  });
}

/**
 * Lookup leve pra forms: retorna `Map<fieldName, FieldRequirement>` com
 * entries do workflow específico + `_global`. Use:
 *
 *   const reqs = useFieldRequirementsLookup('comercial', 'card');
 *   const isRequired = reqs.get('valor')?.required ?? false;
 *
 * Pra checar 1 campo só, usar `useIsFieldRequired` (atalho).
 *
 * Em loading, retorna Map vazio (UI cai pro default: nada obrigatório
 * além das regras Zod hardcoded). Não bloqueia render.
 *
 * AUDITORIA-V2-002 / C1 (2026-05-25): GET é PÚBLICO (requireAuth apenas) —
 * qualquer user autenticado pode ler config pra renderizar form corretamente.
 * Antes exigia requireAdmin e non-admin caía em 403 silencioso → form
 * bloqueava submit indevidamente. Agora vendedor + admin recebem mesma
 * config. Escrita (PUT) continua admin-only.
 *
 * Em loading inicial, retorna Map vazio (UI cai pro default canônico:
 * tudo opcional, AUDITORIA-V2-002 / C2). Não bloqueia render.
 */
export function useFieldRequirementsLookup(
  workflow: string,
  entityType: FieldRequirementEntityType
): Map<string, FieldRequirement> {
  // Pede o entityType específico — pode ter entries em workflow específico
  // E em `_global`. Backend retorna intercalado; filtramos client-side.
  const { data: rows = [] } = useFieldRequirements({ entityType });

  return useMemo(() => {
    const map = new Map<string, FieldRequirement>();
    for (const r of rows) {
      if (r.workflowSlug !== workflow && r.workflowSlug !== '_global') continue;
      // F1: consumidores atuais (asteriscos dos forms) leem SÓ a régua global;
      // as réguas por etiqueta entram nos forms na F4, quando eles souberem
      // mostrar os campos. Sem este filtro, uma régua de Funcionário poria
      // asterisco no form de todo mundo.
      if ((r.etiqueta ?? '') !== '') continue;
      // Workflow específico tem precedência sobre `_global` se ambos
      // existirem com o mesmo fieldName (raro, mas defensivo).
      if (map.has(r.fieldName) && r.workflowSlug === '_global') continue;
      map.set(r.fieldName, r);
    }
    return map;
  }, [rows, workflow]);
}

/**
 * Atalho pra `useFieldRequirementsLookup(...).get(fieldName)?.required`.
 *
 * Uso típico no JSX do form:
 *
 *   const isTituloReq = useIsFieldRequired('comercial', 'card', 'titulo');
 *   <Label required={isTituloReq}>Título</Label>
 *
 * NOTA: como cada chamada do hook dispara o mesmo `useFieldRequirements`
 * sob o capô, e React Query dedupa por queryKey, chamar várias vezes no
 * mesmo render é seguro (1 fetch só, 1 cache hit nos demais).
 */
/**
 * F2 (mig 470): o CONJUNTO de campos exigidos pra um cadastro com estas
 * etiquetas — união régua global ('') ∪ réguas das etiquetas. A ficha usa
 * pra asterisco e pra recusa falada antes do PATCH; o servidor revalida.
 */
export function useExigidosParaEtiquetas(
  etiquetas: string[],
  opts?: {
    /**
     * 🔴 CADASTRO-RAPIDO-E-MARKUP (31/08/2026): só os campos `fixed` — os que o
     * admin não pode desmarcar porque o registro não existe sem eles.
     *
     * É o recorte do NASCIMENTO do cadastro, o mesmo que a rota usa
     * (`assertRequiredFields({ apenasFixos: true })`). A régua inteira não
     * sumiu: passou a ser cobrada no Ganho. Tela e servidor precisam usar o
     * MESMO recorte — foi o aviso literal do handoff, e um recorte só na tela
     * deixaria o servidor recusando o cadastro em silêncio.
     */
    apenasFixos?: boolean;
  },
): Set<string> {
  const { data: rows = [] } = useFieldRequirements({ entityType: 'client' });
  const chave = etiquetas.join('|');
  const apenasFixos = opts?.apenasFixos ?? false;
  return useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (!r.required) continue;
      if (apenasFixos && !r.fixed) continue;
      const et = r.etiqueta ?? '';
      if (et === '' || etiquetas.includes(et)) set.add(r.fieldName);
    }
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, chave, apenasFixos]);
}

export function useIsFieldRequired(
  workflow: string,
  entityType: FieldRequirementEntityType,
  fieldName: string
): boolean {
  const lookup = useFieldRequirementsLookup(workflow, entityType);
  return lookup.get(fieldName)?.required ?? false;
}

// ─── Actions ────────────────────────────────────────────────────────────

export function useFieldRequirementActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateAll = () => qc.invalidateQueries({ queryKey: FIELD_REQ_KEYS.all });

  const upsert = useCallback(
    async (input: UpsertFieldRequirementInput): Promise<FieldRequirement> => {
      const key = `${input.workflowSlug}:${input.entityType}:${input.etiqueta ?? ''}:${input.fieldName}`;
      setPending(key);
      try {
        const result = await api.put<FieldRequirement>(
          '/api/v1/admin/field-requirements',
          input
        );
        await invalidateAll();
        return result;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  return { upsert, pending };
}
